import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Shield, ArrowLeft, RefreshCw, AlertTriangle, MessageSquare, Settings, ListChecks, Activity } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

type Tab = "comments" | "rules" | "settings" | "activity";

interface PageDetail {
  pageId: string;
  name: string;
  owner: { id: string; name: string; email: string; phone?: string } | null;
  isActive: boolean;
  isConnected: boolean;
  disconnectedAt?: string | null;
  tokenValid: boolean;
  webhook: { isSubscribed: boolean; lastWebhookReceived: string | null; lastError: string | null };
  aiSettings: {
    autoRepliesEnabled?: boolean;
    commentModerationEnabled?: boolean;
    aiMode?: string;
    maxAutoRepliesPerCustomer?: number;
    languagePreference?: string;
  };
  moderationRulesCount: number;
  counts: {
    commentsTotal: number;
    byStatus: Record<string, number>;
    errors24h: number;
    moderated24h: number;
    autoReplies24h: number;
  };
}

interface Comment {
  _id: string;
  commentId: string;
  message: string;
  from?: { id?: string; name?: string; picture?: string };
  created_time: string;
  moderationStatus: "pending" | "approved" | "hidden" | "deleted" | "error";
  moderationActions?: Array<{
    action: string;
    reason?: string;
    matchedRule?: string;
    confidence?: number;
    timestamp?: string;
    moderatedBy?: string;
    successful?: boolean;
    autoReplyMessage?: string;
  }>;
  aiAnalysis?: { classification?: string; confidence?: number; details?: string };
  autoReply?: { message?: string; type?: string; sentAt?: string; successful?: boolean };
  permalink_url?: string;
  postId?: string;
}

interface Rule {
  id: string;
  name?: string;
  scope?: string;
  trigger?: { type?: string; value?: string };
  actions?: Array<{ type: string; messageType?: string; autoReply?: string }>;
  language?: string;
  useAI?: boolean;
  isActive?: boolean;
}

interface ActivityEvent {
  _id: string;
  eventType: string;
  timestamp?: string;
  createdAt?: string;
  metadata?: any;
  details?: any;
}

const AdminPageDetail: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("comments");
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [comments, setComments] = useState<Comment[]>([]);
  const [cmtTotal, setCmtTotal] = useState(0);
  const [cmtPage, setCmtPage] = useState(1);
  const [cmtStatus, setCmtStatus] = useState<"all" | "pending" | "approved" | "hidden" | "deleted" | "error">("all");
  const [cmtSearch, setCmtSearch] = useState("");
  const [cmtLoading, setCmtLoading] = useState(false);

  const [rules, setRules] = useState<Rule[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);

  const [replyOpenFor, setReplyOpenFor] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const headers = useMemo((): Record<string, string> => {
    const token = localStorage.getItem("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const apiGet = useCallback(async (path: string) => {
    const res = await fetch(`${API_BASE_URL}${path}`, { headers });
    if (res.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      navigate("/admin/login");
      throw new Error("unauthorized");
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Request failed: ${res.status}`);
    }
    return res.json();
  }, [headers, navigate]);

  const fetchDetail = useCallback(async () => {
    const data = await apiGet(`/api/admin/pages/${pageId}`);
    setDetail(data.page);
  }, [apiGet, pageId]);

  const fetchComments = useCallback(async (pageNum = 1) => {
    setCmtLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pageNum), limit: "50" });
      if (cmtStatus !== "all") params.set("status", cmtStatus);
      if (cmtSearch.trim()) params.set("search", cmtSearch.trim());
      const data = await apiGet(`/api/admin/pages/${pageId}/comments?${params}`);
      setComments(data.comments || []);
      setCmtTotal(data.pagination?.total || 0);
      setCmtPage(data.pagination?.page || pageNum);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCmtLoading(false);
    }
  }, [apiGet, pageId, cmtStatus, cmtSearch]);

  const fetchRules = useCallback(async () => {
    const data = await apiGet(`/api/admin/pages/${pageId}/rules`);
    setRules(data.rules || []);
  }, [apiGet, pageId]);

  const fetchActivity = useCallback(async () => {
    const data = await apiGet(`/api/admin/pages/${pageId}/activity?hours=24&limit=200`);
    setActivity(data.events || []);
  }, [apiGet, pageId]);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    fetchDetail()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [fetchDetail, navigate]);

  useEffect(() => {
    if (!pageId) return;
    if (tab === "comments") fetchComments(1);
    if (tab === "rules") fetchRules().catch((e) => setError(e.message));
    if (tab === "activity") fetchActivity().catch((e) => setError(e.message));
  }, [tab, pageId, fetchComments, fetchRules, fetchActivity]);

  const moderate = async (commentId: string, action: "approve" | "hide" | "delete" | "reply", message?: string) => {
    if (action === "delete" && !window.confirm("Delete this comment on Facebook? This cannot be undone.")) return;
    setActionInFlight(commentId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/pages/${pageId}/comments/${commentId}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(message ? { message } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Action failed: ${data.error || res.status}`);
        return;
      }
      setReplyOpenFor(null);
      setReplyMessage("");
      await fetchComments(cmtPage);
    } finally {
      setActionInFlight(null);
    }
  };

  const updateAi = async (patch: Partial<PageDetail["aiSettings"]>) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/pages/${pageId}/ai`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(`Update failed: ${data.error || res.status}`);
        return;
      }
      setDetail((d) => d ? { ...d, aiSettings: { ...d.aiSettings, ...data.aiSettings } } : d);
    } catch (e: any) {
      alert(`Update failed: ${e.message}`);
    }
  };

  const formatDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : "—");

  if (loading) return <div className="p-6 text-gray-500">Loading…</div>;
  if (!detail) return (
    <div className="p-6">
      <div className="text-red-700 mb-4">{error || "Page not found"}</div>
      <Link to="/admin/pages" className="text-blue-600 underline">Back to pages</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="h-6 w-6" /> Admin Panel
          </h1>
          <div className="flex gap-2">
            <button onClick={() => navigate("/admin/dashboard")} className="px-3 py-1 rounded bg-white border">Dashboard</button>
            <button onClick={() => navigate("/admin/users")} className="px-3 py-1 rounded bg-white border">Users</button>
            <button onClick={() => navigate("/admin/pages")} className="px-3 py-1 rounded bg-white border">Pages</button>
          </div>
        </div>

        <Link to="/admin/pages" className="inline-flex items-center text-sm text-blue-700 mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> All pages</Link>

        <div className="bg-white rounded border p-4 mb-4">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xl font-semibold">{detail.name || "(unnamed)"}</div>
              <div className="text-xs text-gray-500 font-mono">{detail.pageId}</div>
              <div className="text-xs text-gray-600 mt-1">
                Owner: {detail.owner ? <span className="font-medium">{detail.owner.name}</span> : "—"}
                {detail.owner?.email && <> · <a href={`mailto:${detail.owner.email}`} className="text-blue-600">{detail.owner.email}</a></>}
                {detail.owner?.phone && <> · {detail.owner.phone}</>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Pill ok={detail.isConnected} good="Connected" bad="Disconnected" />
              <Pill ok={detail.tokenValid} good="Token valid" bad="Token invalid" />
              <Pill ok={detail.webhook.isSubscribed} good="Webhook on" bad="Webhook off" />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Comments" value={detail.counts.commentsTotal} />
            <Stat label="Hidden" value={detail.counts.byStatus.hidden || 0} tone="amber" />
            <Stat label="Deleted" value={detail.counts.byStatus.deleted || 0} tone="red" />
            <Stat label="Moderated 24h" value={detail.counts.moderated24h} />
            <Stat label="Errors 24h" value={detail.counts.errors24h} tone={detail.counts.errors24h > 0 ? "red" : "gray"} />
          </div>

          <div className="mt-4 flex gap-3 flex-wrap items-center border-t pt-3">
            <span className="text-xs uppercase tracking-wide text-gray-500">Kill switch:</span>
            <KillSwitch
              label="Comment moderation"
              on={detail.aiSettings.commentModerationEnabled !== false}
              onToggle={(v) => updateAi({ commentModerationEnabled: v })}
            />
            <KillSwitch
              label="Auto replies"
              on={detail.aiSettings.autoRepliesEnabled !== false}
              onToggle={(v) => updateAi({ autoRepliesEnabled: v })}
            />
            <button onClick={fetchDetail} className="ml-auto px-2 py-1 text-xs rounded border bg-white inline-flex items-center"><RefreshCw className="h-3 w-3 mr-1" /> Refresh</button>
          </div>

          {detail.webhook.lastError && (
            <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-800">
              <AlertTriangle className="inline h-3 w-3 mr-1" /> Webhook error: {detail.webhook.lastError}
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          <TabBtn current={tab} value="comments" onClick={setTab}><MessageSquare className="inline h-4 w-4 mr-1" />Comments ({cmtTotal || detail.counts.commentsTotal})</TabBtn>
          <TabBtn current={tab} value="rules" onClick={setTab}><ListChecks className="inline h-4 w-4 mr-1" />Rules ({detail.moderationRulesCount})</TabBtn>
          <TabBtn current={tab} value="settings" onClick={setTab}><Settings className="inline h-4 w-4 mr-1" />AI Settings</TabBtn>
          <TabBtn current={tab} value="activity" onClick={setTab}><Activity className="inline h-4 w-4 mr-1" />Activity</TabBtn>
        </div>

        {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

        {tab === "comments" && (
          <div>
            <div className="flex gap-2 items-end mb-3 flex-wrap">
              <div>
                <label className="text-xs text-gray-500">Status</label>
                <select className="border rounded px-2 py-1" value={cmtStatus} onChange={(e) => setCmtStatus(e.target.value as any)}>
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="hidden">Hidden</option>
                  <option value="deleted">Deleted</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Search</label>
                <input className="border rounded px-2 py-1 w-64" value={cmtSearch} onChange={(e) => setCmtSearch(e.target.value)} placeholder="message or author" />
              </div>
              <button onClick={() => fetchComments(1)} className="px-3 py-1 rounded bg-gray-900 text-white" disabled={cmtLoading}>Filter</button>
              <div className="text-xs text-gray-600 ml-auto pb-1">{cmtTotal} comments</div>
            </div>

            <div className="space-y-3">
              {comments.map((c) => (
                <div key={c.commentId} className="bg-white border rounded p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{c.from?.name || "Unknown"}</span>
                        <span className="mx-2">·</span>
                        {formatDate(c.created_time)}
                        <span className="mx-2">·</span>
                        <StatusPill status={c.moderationStatus} />
                        {c.permalink_url && (<><span className="mx-2">·</span><a href={c.permalink_url} target="_blank" rel="noreferrer" className="text-blue-600">View on Facebook</a></>)}
                      </div>
                      <div className="mt-2 text-sm whitespace-pre-wrap break-words">{c.message || <span className="text-gray-400">(no text)</span>}</div>

                      {(c.aiAnalysis?.classification || c.aiAnalysis?.details) && (
                        <div className="mt-2 text-xs bg-blue-50 border border-blue-100 rounded p-2">
                          <span className="font-medium">AI:</span>{" "}
                          {c.aiAnalysis.classification && <span className="mr-2">{c.aiAnalysis.classification}{c.aiAnalysis.confidence != null && ` (${Math.round((c.aiAnalysis.confidence || 0) * 100)}%)`}</span>}
                          {c.aiAnalysis.details && <span className="text-gray-700">{c.aiAnalysis.details}</span>}
                        </div>
                      )}

                      {c.autoReply?.message && (
                        <div className="mt-2 text-xs bg-green-50 border border-green-100 rounded p-2">
                          <span className="font-medium">Auto-reply{c.autoReply.successful === false ? " (failed)" : ""}:</span> {c.autoReply.message}
                        </div>
                      )}

                      {Array.isArray(c.moderationActions) && c.moderationActions.length > 0 && (
                        <details className="mt-2 text-xs text-gray-600">
                          <summary className="cursor-pointer">Moderation history ({c.moderationActions.length})</summary>
                          <ul className="mt-1 ml-4 list-disc space-y-1">
                            {c.moderationActions.map((a, i) => (
                              <li key={i}>
                                <span className="font-mono">{a.action}</span>
                                {a.reason && <> · {a.reason}</>}
                                {a.matchedRule && <> · rule: {a.matchedRule}</>}
                                {a.moderatedBy && <> · by {a.moderatedBy}</>}
                                {a.timestamp && <> · {formatDate(a.timestamp)}</>}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>

                    <div className="flex flex-col gap-1 shrink-0">
                      <button disabled={actionInFlight === c.commentId} onClick={() => moderate(c.commentId, "approve")} className="px-2 py-1 text-xs border rounded">Approve</button>
                      <button disabled={actionInFlight === c.commentId} onClick={() => moderate(c.commentId, "hide")} className="px-2 py-1 text-xs border rounded">Hide</button>
                      <button disabled={actionInFlight === c.commentId} onClick={() => moderate(c.commentId, "delete")} className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded">Delete</button>
                      <button disabled={actionInFlight === c.commentId} onClick={() => { setReplyOpenFor(c.commentId); setReplyMessage(""); }} className="px-2 py-1 text-xs border rounded">Reply</button>
                    </div>
                  </div>

                  {replyOpenFor === c.commentId && (
                    <div className="mt-3 border-t pt-3">
                      <textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        className="w-full border rounded p-2 text-sm"
                        rows={3}
                        placeholder="Your reply (posted publicly under the comment)"
                      />
                      <div className="mt-2 flex gap-2">
                        <button
                          disabled={!replyMessage.trim() || actionInFlight === c.commentId}
                          onClick={() => moderate(c.commentId, "reply", replyMessage)}
                          className="px-3 py-1 text-sm rounded bg-gray-900 text-white disabled:opacity-50"
                        >Send reply</button>
                        <button onClick={() => { setReplyOpenFor(null); setReplyMessage(""); }} className="px-3 py-1 text-sm rounded border">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {comments.length === 0 && !cmtLoading && (
                <div className="bg-white border rounded p-6 text-center text-gray-500">No comments match.</div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <button className="px-2 py-1 border rounded" onClick={() => fetchComments(Math.max(1, cmtPage - 1))} disabled={cmtPage <= 1 || cmtLoading}>Prev</button>
              <div className="text-xs text-gray-600">Page {cmtPage} / {Math.max(1, Math.ceil(cmtTotal / 50))}</div>
              <button className="px-2 py-1 border rounded" onClick={() => fetchComments(cmtPage + 1)} disabled={cmtPage >= Math.ceil(cmtTotal / 50) || cmtLoading}>Next</button>
            </div>
          </div>
        )}

        {tab === "rules" && (
          <div className="space-y-3">
            {rules.map((r) => (
              <div key={r.id} className="bg-white border rounded p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium">{r.name || "(unnamed)"} <span className="ml-2 text-xs font-mono text-gray-500">{r.id}</span></div>
                    <div className="text-xs text-gray-600">
                      scope: {r.scope || "—"} · trigger: {r.trigger?.type || "—"}{r.trigger?.value ? ` (${r.trigger.value})` : ""} · language: {r.language || "auto"} · useAI: {String(!!r.useAI)}
                    </div>
                  </div>
                  <Pill ok={r.isActive !== false} good="Active" bad="Inactive" />
                </div>
                {Array.isArray(r.actions) && r.actions.length > 0 && (
                  <div className="mt-2 text-xs">
                    Actions: {r.actions.map((a, i) => (
                      <span key={i} className="inline-block bg-gray-100 rounded px-2 py-0.5 mr-1">
                        {a.type}{a.messageType ? ` · ${a.messageType}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {rules.length === 0 && (
              <div className="bg-white border rounded p-6 text-center text-gray-500">No rules configured.</div>
            )}
            <div className="text-xs text-gray-500 mt-2">Read-only for now. Rule editing coming in phase 2.</div>
          </div>
        )}

        {tab === "settings" && (
          <div className="bg-white border rounded p-4 max-w-2xl">
            <div className="grid gap-4">
              <SettingRow label="Comment moderation">
                <KillSwitch label="" on={detail.aiSettings.commentModerationEnabled !== false} onToggle={(v) => updateAi({ commentModerationEnabled: v })} />
              </SettingRow>
              <SettingRow label="Auto replies">
                <KillSwitch label="" on={detail.aiSettings.autoRepliesEnabled !== false} onToggle={(v) => updateAi({ autoRepliesEnabled: v })} />
              </SettingRow>
              <SettingRow label="AI mode">
                <select
                  className="border rounded px-2 py-1"
                  value={detail.aiSettings.aiMode || "instructions_plus_auto"}
                  onChange={(e) => updateAi({ aiMode: e.target.value })}
                >
                  <option value="instructions_only">instructions_only</option>
                  <option value="instructions_plus_auto">instructions_plus_auto</option>
                </select>
              </SettingRow>
              <SettingRow label="Max auto-replies per customer">
                <input
                  type="number"
                  min={0}
                  defaultValue={detail.aiSettings.maxAutoRepliesPerCustomer ?? 5}
                  className="border rounded px-2 py-1 w-24"
                  onBlur={(e) => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v) && v !== detail.aiSettings.maxAutoRepliesPerCustomer) updateAi({ maxAutoRepliesPerCustomer: v });
                  }}
                />
              </SettingRow>
              <SettingRow label="Language preference">
                <div className="text-sm text-gray-600">{detail.aiSettings.languagePreference || "auto"} <span className="text-xs text-gray-400">(read-only)</span></div>
              </SettingRow>
            </div>
          </div>
        )}

        {tab === "activity" && (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">When</th>
                  <th className="p-2">Event</th>
                  <th className="p-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((e) => (
                  <tr key={e._id} className="border-t align-top">
                    <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(e.timestamp || e.createdAt)}</td>
                    <td className="p-2 font-mono text-xs">{e.eventType}</td>
                    <td className="p-2 text-xs">
                      <pre className="bg-gray-50 rounded p-2 max-w-2xl overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(e.metadata || e.details || {}, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
                {activity.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={3}>No activity in the last 24h.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Pill: React.FC<{ ok: boolean; good: string; bad: string }> = ({ ok, good, bad }) => (
  <span className={`px-2 py-0.5 rounded text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{ok ? good : bad}</span>
);

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const tone: Record<string, string> = {
    pending: "bg-gray-100 text-gray-700",
    approved: "bg-green-100 text-green-700",
    hidden: "bg-amber-100 text-amber-800",
    deleted: "bg-red-100 text-red-700",
    error: "bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-0.5 rounded text-xs ${tone[status] || "bg-gray-100 text-gray-700"}`}>{status}</span>;
};

const TabBtn: React.FC<{ current: Tab; value: Tab; onClick: (t: Tab) => void; children: React.ReactNode }> = ({ current, value, onClick, children }) => (
  <button onClick={() => onClick(value)} className={`px-3 py-1 rounded ${current === value ? "bg-gray-900 text-white" : "bg-white border"}`}>{children}</button>
);

const Stat: React.FC<{ label: string; value: number; tone?: "gray" | "red" | "amber" }> = ({ label, value, tone = "gray" }) => {
  const toneClass = tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-700" : "text-gray-800";
  return (
    <div className="bg-gray-50 rounded p-2 border">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};

const KillSwitch: React.FC<{ label: string; on: boolean; onToggle: (v: boolean) => void }> = ({ label, on, onToggle }) => (
  <button
    onClick={() => onToggle(!on)}
    className={`px-3 py-1 rounded text-xs border ${on ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}
    title={`Click to ${on ? "disable" : "enable"}`}
  >
    {label && <span className="mr-1">{label}:</span>}{on ? "ON" : "OFF"}
  </button>
);

const SettingRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="text-sm text-gray-700">{label}</div>
    <div>{children}</div>
  </div>
);

export default AdminPageDetail;
